import { FlexProps } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'ui-components';
import authApi from '~/authApi';
import clientApi from '~/clientApi';
import { Creator } from '~/clientApi/creator.model';
import MediaPackDeleteModal from '~/components/MediaPackDeleteModal';
import {
  FEATURE_FLAGS,
  LOCALSTORAGE_KEY_IMPERSONATOR_ID,
  SNACKBAR_ERROR_GENERIC_OPTIONS,
  USER_ROLES,
} from '~/constants';
import { routes } from '~/core/router';
import { LocalStorageService } from '~/core/utils/storage';
import { closeModal, openModal } from '~/ducks/ui';
import { MEDIA_PACK } from '~/ducks/ui.constants';
import { captureException } from '~/errorsLogging';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { MEDIA_PACKS_COLLECTION_QUERY_KEY } from '~/queries/keys';
import { MediaPackDetailEditModal } from '~/screens/MediaPackDetailDefaultScreen/components/MediaPackDetailEditModal';
import { selectCreator } from '~/selectors/creator';
import { selectOpenModals } from '~/selectors/ui';

import { NavBar } from './NavBar';
import { NavBarProps } from './NavBar.model';
import { MenuItem } from './components/ActionsDropDown/Actions.types';

interface Props extends Omit<NavBarProps, 'activeSection'>, FlexProps {}

export const NavBarContainer: React.FC<Props> = ({
  creatorBenchmarks,
  creatorInsights,
  mediaPack,
  pdfImages,
  ...props
}) => {
  const queryClient = useQueryClient();
  const [mediaPackToDelete, setMediaPackToDelete] = useState<string | null>(
    null,
  );
  const router = useRouter();
  const creator = useSelector(selectCreator);
  const snackbar = useSnackbar();
  const dispatch = useDispatch();
  const { creatorSlug } = router.query;
  const storedImpersonatorId = LocalStorageService.getItem(
    LOCALSTORAGE_KEY_IMPERSONATOR_ID,
  );

  /* 
  When you connect/reconnect a social network, the creator that is in the auth.context changes. 
  This causes the component to re-render again by setting the isOpen of the modal to false.
  To solve this we have implemented a global state for modal states in redux.
  */
  const openModals = useSelector(selectOpenModals);
  const isModalOpen = openModals[MEDIA_PACK.EDIT_MODAL_ID];

  const handleOpenModal = () => {
    dispatch(openModal(MEDIA_PACK.EDIT_MODAL_ID));
  };

  const handleCloseModal = () => dispatch(closeModal(MEDIA_PACK.EDIT_MODAL_ID));

  const handleDeleteConfirmationModal = (id: string) =>
    setMediaPackToDelete(id);

  const handleDeleteMediaPack = async (id: string) => {
    try {
      await clientApi.mediaPacks.delete({ id, _impersonateOnce: creatorSlug });
      await queryClient.invalidateQueries({
        queryKey: [MEDIA_PACKS_COLLECTION_QUERY_KEY],
      });

      if (creatorSlug) {
        // TODO add routes
        await router.push(`/t/${creatorSlug}/media-packs`);
      } else {
        await router.push(routes.mediaPacks);
      }
    } catch (error) {
      snackbar.error({ ...SNACKBAR_ERROR_GENERIC_OPTIONS });
      captureException(error);
    }
  };

  const handleDuplicateMediaPack = async ({
    id,
    name,
  }: {
    id: string;
    name: string;
  }) => {
    try {
      const newMediaPack = await clientApi.mediaPacks.duplicate({
        id,
        _impersonateOnce: creatorSlug as string,
      });

      await queryClient.invalidateQueries({
        queryKey: [MEDIA_PACKS_COLLECTION_QUERY_KEY],
      });
      const mediaPackUrl = creatorSlug
        ? // TODO add routes
          `/t/${creatorSlug}/media-packs/${newMediaPack.id}`
        : `/media-packs/${newMediaPack.id}`;

      snackbar.success({
        description: `${name} has been successfully duplicated`,
        action: {
          label: 'See Media Pack',
          onClick: () => router.push(mediaPackUrl),
        },
        duration: 9000,
        isCloseable: true,
      });
    } catch (error) {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
      captureException(error);
    }
  };

  const { isEnabled: isMediaPacksDeprecated } = useFeature(
    FEATURE_FLAGS.foamDeprecateMediaPacks,
  );
  const { isEnabled: isMediaKitsForCreatorsEnabled } = useFeature(
    FEATURE_FLAGS.foamCreatorMediaKits,
  );

  const userIsManager = authApi.user.roles.includes(USER_ROLES.talentManager);

  const dropDownMenuActions = () => {
    const editMenuItem: MenuItem = {
      label: 'Edit Media Pack',
      onClick: handleOpenModal,
    };
    const removeMenuItem: MenuItem = {
      label: 'Remove',
      onClick: () => handleDeleteConfirmationModal(mediaPack.id),
    };
    const duplicateMenuItem: MenuItem = {
      label: 'Duplicate',
      onClick: () =>
        handleDuplicateMediaPack({ id: mediaPack.id, name: mediaPack.name }),
    };
    if (userIsManager && isMediaPacksDeprecated) {
      // Remove ability to create/remove media packs for managers
      return [removeMenuItem];
    }

    if (!userIsManager && isMediaKitsForCreatorsEnabled) {
      // Remove ability to edit/duplicate media packs for creators
      return [removeMenuItem];
    }

    return [editMenuItem, duplicateMenuItem, removeMenuItem];
  };

  return (
    <>
      <MediaPackDetailEditModal
        creator={creator as Creator}
        isOpen={isModalOpen}
        mediaPack={mediaPack}
        onClose={handleCloseModal}
      />

      <MediaPackDeleteModal
        linkedRostersNames={mediaPack?.linkedRostersNames}
        isOpen={!!mediaPackToDelete}
        onConfirm={() => handleDeleteMediaPack(mediaPack.id)}
        onCancel={() => setMediaPackToDelete(null)}
      />

      <NavBar
        creatorBenchmarks={creatorBenchmarks}
        creatorInsights={creatorInsights}
        mediaPack={mediaPack}
        pdfImages={pdfImages}
        creator={creator as Creator}
        dropDownMenuActions={dropDownMenuActions()}
        top={storedImpersonatorId ? 10 : 0}
        creatorSlugParam={creatorSlug as string}
        {...props}
      />
    </>
  );
};
